// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';
import { IUrlShortener } from '../../tools/share/iurlshortener';
import { noop } from '../../tools/utils/async';

class AddBookmarkComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = ['../../styles/common.css'];

  private readonly isMac: boolean = navigator.userAgent.includes('Mac');
  protected awaitingBookmarkLink: boolean = false;
  private showingModal: boolean = false;

  private bookmarkLink: string | undefined;

  get urlShortener(): IUrlShortener | undefined {
    return this.context.shareManager.UrlShortener;
  }

  public constructor() {
    super('addbookmark');
    this.closeOnKeyUp = this.closeOnKeyUp.bind(this);
    this.handleBookmarkShortcut = this.handleBookmarkShortcut.bind(this);
  }

  override render() {
    super.render();
  }

  protected override connectedCallback() {
    super.connectedCallback();
    this.render();
    super.girafeTranslate();
    globalThis.addEventListener('keydown', this.handleBookmarkShortcut);
  }

  protected override disconnectedCallback() {
    super.disconnectedCallback();
    globalThis.removeEventListener('keydown', this.handleBookmarkShortcut);
  }

  public async addBookmark() {
    this.showingModal = true;
    const currentUrl = globalThis.location.href;
    await this.generateBookmarkLink();
    if (!this.bookmarkLink) {
      return;
    }

    const messages: string[] = ['create-bookmark-by-shortcut'];

    // Change URL so that this is used to create a bookmark when the User presses the Shortcut
    globalThis.history.pushState({}, '', this.bookmarkLink);
    const shortcut = this.isMac ? '⌘ + D' : 'Ctrl + D';
    globalThis.addEventListener('keydown', this.closeOnKeyUp);
    window
      .gInfo(messages, 'create-bookmark', (translatedMessage: string | string[]) => {
        return Array.isArray(translatedMessage)
          ? translatedMessage.map((message) => message.replace('${shortcut}', shortcut)).join('<br /><br />')
          : translatedMessage;
      })
      .then(() => {
        this.showingModal = false;
        globalThis.removeEventListener('keydown', this.closeOnKeyUp);
        // Restore the URL afterwards
        globalThis.history.pushState({}, '', currentUrl);
      });
  }

  private async generateBookmarkLink() {
    if (!this.urlShortener) {
      return;
    }

    this.awaitingBookmarkLink = true;
    this.refreshRender();

    const baseUrl = this.context.urlManager.getBaseUrlPath();
    const hash = this.context.shareManager.getStateToShare();

    const bookmarkUrl = `${baseUrl}#${hash}`;
    const response = await this.urlShortener.shortenUrl(bookmarkUrl);
    if (response.success) {
      this.bookmarkLink = response.shorturl;
    }
    this.awaitingBookmarkLink = false;
    this.refreshRender();
  }

  private closeOnKeyUp(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      window.gCloseModal();
    }
  }

  private handleBookmarkShortcut(event: KeyboardEvent) {
    const addBookmarkShortcutPressed =
      event.key === 'd' && ((!this.isMac && event.ctrlKey) || (this.isMac && event.metaKey));
    if (addBookmarkShortcutPressed && !this.showingModal) {
      event.preventDefault();
      this.addBookmark().then(noop);
    }
  }
}

export default AddBookmarkComponent;
